import ufeApi from 'services/api/ufeApi';
import urlUtils from 'utils/Url';
import headerUtils from 'utils/Headers';
import Empty from 'constants/empty';
import { SAVE_FOR_LATER_LIST_TYPE, DEFAULT_SHOPPING_LIST_NAME, SAVE_FOR_LATER_MAX_ITEMS } from 'constants/saveForLater';

const { userXTimestampHeader } = headerUtils;

/**
 * Handle API response by checking for errors and rejecting on failure.
 * @param {Promise} promise - The API request promise to handle
 * @returns {Promise<object>} Promise resolving to response data or rejecting on error
 */
function handleResponse(promise) {
    return promise
        .then(data => {
            if (data?.errorCode || data?.error) {
                return Promise.reject(data);
            }

            return data;
        })
        .catch(err => {
            return Promise.reject(err);
        });
}

/**
 * Fetch shopping lists for a profile (Save For Later context or generic lists).
 * @param {string|number} profileId - User profile identifier
 * @param {object} [options] - Optional query parameters
 * @param {number} [options.currentPage=1] - Current page number for pagination
 * @param {number} [options.includeInactiveSkus=0] - Whether to include inactive SKUs (0 or 1)
 * @param {number} [options.itemsPerPage=50] - Number of items per page
 * @param {string} [options.sortBy='recently'] - Sort order for items
 * @param {string} [options.type='save_for_later'] - List type to fetch
 * @returns {Promise<object>} Promise resolving to lists data
 */
function getLists(profileId, options = Empty.Object) {
    let url = `/gway/v1/dotcom/users/profiles/${profileId}/lists/fetch`;

    const qsParams = {
        currentPage: options.currentPage || 1,
        includeInactiveSkus: options.includeInactiveSkus || 0,
        itemsPerPage: options.itemsPerPage || SAVE_FOR_LATER_MAX_ITEMS,
        sortBy: options.sortBy || 'recently',
        type: (options.type || SAVE_FOR_LATER_LIST_TYPE).toLowerCase()
    };

    const qs = urlUtils.makeQueryString(qsParams);
    url += qs ? `?${qs}` : '';

    return handleResponse(ufeApi.makeRequest(url, { method: 'GET' }));
}

/**
 * Add an item to a list (Save For Later or other type).
 * @param {string|number} profileId - User profile identifier
 * @param {object} payload - Item to add to list
 * @param {string} [payload.source='productPage'] - Source of the add action (e.g., 'productPage', 'basket')
 * @param {string} payload.productId - Product identifier (e.g., 'P505016')
 * @param {string} payload.skuId - SKU identifier (e.g., '2632834')
 * @param {string} [payload.shoppingListType='save_for_later'] - List type ('save_for_later' or 'loves')
 * @param {string} payload.shoppingListId - Shopping list ID (required unless creating new list)
 * @returns {Promise<object>} Promise resolving to API response
 */
function addItem(profileId, payload = Empty.Object) {
    const url = `/gway/v1/dotcom/users/profiles/${profileId}/lists`;

    const normalized = {
        source: payload.source || 'productPage',
        productId: payload.productId,
        skuId: payload.skuId,
        shoppingListType: (payload.shoppingListType || SAVE_FOR_LATER_LIST_TYPE).toLowerCase(),
        shoppingListId: payload.shoppingListId
    };

    // Remove undefined keys to keep body minimal
    Object.keys(normalized).forEach(key => normalized[key] === undefined && delete normalized[key]);

    const body = JSON.stringify(normalized);

    return handleResponse(
        ufeApi.makeRequest(url, {
            method: 'POST',
            headers: userXTimestampHeader(),
            body
        })
    );
}

/**
 * Search items across lists (e.g., for dedupe or quick lookup).
 * @param {string|number} profileId - User profile identifier
 * @param {object} payload - Search criteria
 * @param {string[]} [payload.skuIds] - Array of SKU IDs to search for
 * @param {string[]} [payload.productIds] - Array of product IDs to search for
 * @returns {Promise<object>} Promise resolving to search results with items array
 */
function searchItems(profileId, payload = Empty.Object) {
    const url = `/gway/v1/dotcom/users/profiles/${profileId}/lists/shopping-list-items`;
    const body = JSON.stringify(payload);

    return handleResponse(
        ufeApi.makeRequest(url, {
            method: 'POST',
            headers: userXTimestampHeader(),
            body
        })
    );
}

/**
 * Create a new list (default type can be provided by caller, e.g., 'SAVE_FOR_LATER').
 * @param {string|number} profileId - User profile identifier
 * @param {object} payload - List creation parameters
 * @param {string} [payload.shoppingListType='save_for_later'] - List type ('save_for_later' or 'loves')
 * @param {string} [payload.shoppingListName='save_for_later List'] - Name for the new list
 * @param {boolean} [payload.isDefault] - Whether this should be the default list
 * @returns {Promise<object>} Promise resolving to created list data
 */
function createList(profileId, payload = Empty.Object) {
    const url = `/gway/v1/dotcom/users/profiles/${profileId}/lists/new`;
    const normalized = {
        shoppingListType: (payload.shoppingListType || SAVE_FOR_LATER_LIST_TYPE).toLowerCase(),
        shoppingListName: payload.shoppingListName || DEFAULT_SHOPPING_LIST_NAME,
        isDefault: payload.isDefault
    };

    Object.keys(normalized).forEach(key => normalized[key] === undefined && delete normalized[key]);
    const body = JSON.stringify(normalized);

    return handleResponse(
        ufeApi.makeRequest(url, {
            method: 'POST',
            headers: userXTimestampHeader(),
            body
        })
    );
}

/**
 * Remove an item from a list.
 * DELETE /users/profiles/{profileId}/lists/skus/{skuId}?productId=Pxxx&type=save_for_later&id=shoppingListId
 * @param {string|number} profileId - User profile identifier
 * @param {object} payload - Item removal parameters
 * @param {string} payload.skuId - SKU identifier to remove (required)
 * @param {string} payload.productId - Product identifier (required)
 * @param {string} payload.shoppingListId - Shopping list ID (required)
 * @param {string} [payload.shoppingListType='save_for_later'] - List type
 * @returns {Promise<object>} Promise resolving to removal confirmation
 */
function removeItem(profileId, payload = Empty.Object) {
    const skuId = payload.skuId;
    const productId = payload.productId;
    const shoppingListId = payload.shoppingListId;
    const type = (payload.shoppingListType || SAVE_FOR_LATER_LIST_TYPE).toLowerCase();

    if (!skuId || !productId || !shoppingListId) {
        return Promise.reject(new Error('Missing required fields for removeItem (skuId, productId, shoppingListId).'));
    }

    const qs = urlUtils.makeQueryString({ productId, type, id: shoppingListId });
    const url = `/gway/v1/dotcom/users/profiles/${profileId}/lists/skus/${skuId}${qs ? `?${qs}` : ''}`;

    return handleResponse(
        ufeApi.makeRequest(url, {
            method: 'DELETE',
            headers: userXTimestampHeader()
        })
    );
}

export default {
    getLists,
    addItem,
    searchItems,
    createList,
    removeItem
};
